import React from 'react';
import { Box, Typography, BoxProps } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { getStatusColor, getStatusLabel, getStatusDescription } from '../../utils/reportPortalToolkit';

interface StatusBadgeProps extends BoxProps {
  status: string;
  variant?: 'pill' | 'dot' | 'text' | 'bordered';
  size?: 'small' | 'medium' | 'large';
  showTooltip?: boolean;
  showDescription?: boolean;
}

const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  variant = 'pill',
  size = 'medium',
  showTooltip = false,
  showDescription = false,
  ...boxProps
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const themeMode = isDarkMode ? 'dark' : 'light';
  
  const bgColor = getStatusColor(status, 'bg', themeMode);
  const textColor = getStatusColor(status, 'text', themeMode);
  const borderColor = getStatusColor(status, 'border', themeMode);
  const label = getStatusLabel(status);
  const description = getStatusDescription(status);

  const getSizeStyles = () => {
    switch (size) {
      case 'small':
        return {
          padding: '2px 8px',
          fontSize: '0.7rem',
          borderRadius: '10px'
        };
      case 'large':
        return {
          padding: '6px 16px',
          fontSize: '0.9rem',
          borderRadius: '20px'
        };
      default: // medium
        return {
          padding: '4px 12px',
          fontSize: '0.8rem',
          borderRadius: '16px'
        };
    }
  };

  const getVariantStyles = () => {
    const baseStyles = {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 600,
      textTransform: 'capitalize' as const,
      letterSpacing: '0.3px',
      transition: 'all 0.2s ease',
      cursor: 'default',
      ...getSizeStyles()
    };

    switch (variant) {
              case 'pill':
        return {
          ...baseStyles,
          backgroundColor: bgColor,
          color: textColor,
          '&:hover': {
            opacity: 0.9,
            transform: 'translateY(-1px)',
            boxShadow: `0 2px 8px ${borderColor}40`
          }
        };
      
      case 'dot':
        return {
          ...baseStyles,
          backgroundColor: bgColor,
          color: textColor,
          borderRadius: '50%',
          width: size === 'small' ? '16px' : size === 'large' ? '24px' : '20px',
          height: size === 'small' ? '16px' : size === 'large' ? '24px' : '20px',
          padding: 0,
          '&:hover': {
            opacity: 0.9,
            transform: 'scale(1.1)'
          }
        };
      
      case 'text':
        return {
          ...baseStyles,
          backgroundColor: 'transparent',
          color: textColor,
          padding: '2px 0',
          '&:hover': {
            opacity: 0.8
          }
        };
      
      case 'bordered':
        return {
          ...baseStyles,
          backgroundColor: 'transparent',
          color: textColor,
          border: `2px solid ${borderColor}`,
          '&:hover': {
            backgroundColor: bgColor,
            opacity: 0.9
          }
        };
      
      default:
        return baseStyles;
    }
  };

  const renderContent = () => {
    switch (variant) {
      case 'dot':
        return null; // Dot variant doesn't show text
      case 'text':
        return (
          <Typography variant="body2" component="span">
            {label}
          </Typography>
        );
      default:
        return (
          <Typography variant="body2" component="span">
            {label}
          </Typography>
        );
    }
  };

  const content = (
    <Box
      sx={getVariantStyles()}
      title={showTooltip ? description : undefined}
      {...boxProps}
    >
      {renderContent()}
    </Box>
  );

  if (showDescription && description) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
        {content}
        <Typography 
          variant="caption" 
          color="text.secondary" 
          sx={{ fontSize: '0.65rem', textAlign: 'center', maxWidth: 120 }}
        >
          {description}
        </Typography>
      </Box>
    );
  }

  return content;
};

export default StatusBadge;
